import { cn } from '@newfront-insurance/core-ui/v2';
import type { ReactNode } from 'react';

import { useAttachments } from './attachments-provider';
import { useChatInputContext } from './chat-input-context';
import { useDragHandlersRef } from './chat-input-context';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';

interface ChatInputProps {
  children: ReactNode;
  className?: string;
}

/**
 * Root wrapper component for chat input.
 * Provides the form container and drag/drop handlers.
 * Handlers come from MessagePayloadProvider (populated by ChatAttachments if rendered).
 *
 * @example
 * ```tsx
 * <ChatInput>
 *   <ChatInputTextarea />
 *   <ChatInputToolbar>
 *     <ChatAttachments />
 *     <SendButton />
 *   </ChatInputToolbar>
 * </ChatInput>
 * ```
 */
export function ChatInput({ children, className }: ChatInputProps) {
  const context = useChatInputContext();
  const { handleSubmit, isPublicMode } = context;
  const { attachments } = useAttachments();
  const { additionalContext } = useChatOverlayContext();
  const hasUploadingFiles = attachments.some((attachment) => attachment.isUploading);
  const successfulAttachmentUuids = attachments
    .filter((attachment) => attachment.fileUuid && !attachment.uploadError)
    .map((attachment) => attachment.fileUuid!);
  const contextAttachmentFileUuids = Array.isArray(additionalContext?.attachmentFileUuids)
    ? additionalContext.attachmentFileUuids.filter((uuid): uuid is string => typeof uuid === 'string')
    : [];
  const hasUnsyncedAttachmentContext =
    successfulAttachmentUuids.length !== contextAttachmentFileUuids.length ||
    successfulAttachmentUuids.some((uuid) => !contextAttachmentFileUuids.includes(uuid));
  // Read drag handlers from ref (populated by ChatAttachments if rendered)
  const handlersRef = useDragHandlersRef();
  const handlers = handlersRef.current;

  const isDragging = handlers.isDragging ?? false;
  const handleDragOver = handlers.handleDragOver ?? (() => {});
  const handleDragLeave = handlers.handleDragLeave ?? (() => {});
  const handleDrop = handlers.handleDrop ?? (() => {});

  return (
    <div className={cn('flex-shrink-0 px-2', className)}>
      <form
        className="mx-auto flex w-full max-w-3xl gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (hasUploadingFiles || hasUnsyncedAttachmentContext) {
            return;
          }
          handleSubmit(e);
        }}
      >
        <div
          className={cn(
            'group relative w-full rounded-lg p-[2px]',
            'bg-border/50',
            'focus-within:bg-lime-blue-gradient',
            {
              'bg-transparent': isDragging,
              'bg-pink-yellow-gradient focus-within:bg-pink-yellow-gradient': isPublicMode,
            },
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="flex w-full flex-col gap-0 overflow-hidden rounded-lg bg-white shadow-[0_0_0_1px_white] group-focus-within:shadow-none">
            {children}
          </div>
        </div>
      </form>
    </div>
  );
}
